/**
 * Field Utilities
 *
 * Shared utilities for field components including:
 * - Data validation (hasValidData)
 * - Schema-based enum label mapping (buildEnumLabelMap, formatEnumLabel, formatValue)
 *
 * Moved from components/medbv/utils/ to be accessible across the application.
 */

// =============================================================================
// DATA VALIDATION
// =============================================================================

/**
 * Check if a data object has any non-empty values.
 *
 * Returns true if the data object contains at least one property with a meaningful value:
 * - Arrays with at least one element
 * - Non-null, non-undefined, non-empty-string values
 *
 * @param data - The data object to check
 * @returns true if the object has at least one valid value, false otherwise
 */
export function hasValidData(data: Record<string, unknown> | undefined | null): boolean {
  if (!data) return false;
  return Object.values(data).some(v => {
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== null && v !== '';
  });
}

// =============================================================================
// SCHEMA ENUM LABEL UTILITIES
// =============================================================================

export interface SchemaOption {
  value: string;
  label: string;
}

/**
 * Type guard to check if an object is a valid schema option
 */
function isSchemaOption(opt: unknown): opt is SchemaOption {
  if (!opt || typeof opt !== 'object') return false;
  const record = opt as Record<string, unknown>;
  return typeof record.value === 'string' && typeof record.label === 'string';
}

/**
 * Process options array and add to map
 */
function processOptions(options: unknown[], map: Record<string, string>): void {
  for (const opt of options) {
    if (isSchemaOption(opt)) {
      map[opt.value] = opt.label;
    }
  }
}

/**
 * Recursively extract options from schema object
 */
function extractOptions(obj: unknown, map: Record<string, string>): void {
  if (Array.isArray(obj)) {
    obj.forEach(item => extractOptions(item, map));
    return;
  }

  if (!obj || typeof obj !== 'object') return;

  const record = obj as Record<string, unknown>;

  // Check if this object has options array with value/label pairs
  if (record.options && Array.isArray(record.options)) {
    processOptions(record.options, map);
  }

  // Recursively search nested objects
  Object.values(record).forEach(value => extractOptions(value, map));
}

/**
 * Build a map of enum value → label from the schema options
 * @param schema - The workflow schema response from the API
 * @returns A map of enum values to their human-readable labels
 */
export function buildEnumLabelMap(schema: unknown): Record<string, string> {
  const map: Record<string, string> = {};
  extractOptions(schema, map);
  return map;
}

/**
 * Convert a snake_case or UPPER_CASE string to Title Case
 * e.g., "PRIOR_AUTH_STATUS_APPROVED" → "Prior Auth Status Approved"
 */
export function humanize(str: string): string {
  return str
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Format an enum value to its human-readable label
 *
 * First checks the schema-derived map for a value→label mapping,
 * falls back to humanizing the raw value if no mapping is found.
 *
 * @param value - The raw enum value
 * @param enumMap - The enum value to label map built from schema
 */
export function formatEnumLabel(value: string, enumMap: Record<string, string>): string {
  return enumMap[value] || humanize(value);
}

/**
 * Generic value formatter that handles all common data types
 *
 * - undefined/null/empty string → "--"
 * - boolean → "Yes" or "No"
 * - array → comma-separated list with enum labels applied
 * - string/number → enum label if found, otherwise string conversion
 *
 * @param value - The value to format
 * @param enumMap - The enum value to label map built from schema
 */
export function formatValue(value: unknown, enumMap: Record<string, string>): string {
  if (value === undefined || value === null || value === '') {
    return '--';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '--';
    return value.map(v => formatEnumLabel(String(v), enumMap)).join(', ');
  }
  return formatEnumLabel(String(value), enumMap);
}

// =============================================================================
// PATH-SCOPED ENUM MAP (fixes numeric value → enum label collisions)
// =============================================================================

/** Field types whose options represent enum mappings */
const ENUM_FIELD_TYPES = new Set(['select', 'multi_select', 'radio']);

/** Path-scoped enum map: "group.field_id" → {value → label} */
export type FieldEnumMap = Record<string, Record<string, string>>;

/**
 * Path-scoped options map: "group.field_id" → the schema's option list, in
 * schema order. This is the raw form {@link FieldEnumMap} is collapsed from:
 * display formatting only needs value → label, but an input control also needs
 * the ORDER and the full set, so it reads this instead.
 */
export type FieldOptionsMap = Record<string, SchemaOption[]>;

interface SchemaField {
  id?: string;
  type?: string;
  options?: unknown[];
  fields?: unknown[];
}

function isSchemaField(obj: unknown): obj is SchemaField {
  return !!obj && typeof obj === 'object';
}

/** Check if a field is an enum type with options and record its option list */
function tryRecordEnumField(
  record: Record<string, unknown>,
  id: string,
  path: string[],
  map: FieldOptionsMap,
): void {
  const type = typeof record.type === 'string' ? record.type : undefined;
  if (!type || !ENUM_FIELD_TYPES.has(type) || !Array.isArray(record.options)) return;

  const options = record.options.filter(isSchemaOption);
  if (options.length > 0) {
    map[[...path, id].join('.')] = options;
  }
}

/** Recurse into nested child arrays (fields, groups) */
function recurseChildren(
  record: Record<string, unknown>,
  currentPath: string[],
  map: FieldOptionsMap,
): void {
  if (Array.isArray(record.fields)) {
    extractFieldEnums(record.fields, currentPath, map);
  }
  if (Array.isArray(record.groups)) {
    extractFieldEnums(record.groups, currentPath, map);
  }
}

/**
 * Recursively walk a schema structure and build a path-scoped options map.
 * Only extracts options from fields with type in ENUM_FIELD_TYPES.
 */
function extractFieldEnums(obj: unknown, path: string[], map: FieldOptionsMap): void {
  if (Array.isArray(obj)) {
    for (const item of obj) {
      extractFieldEnums(item, path, map);
    }
    return;
  }

  if (!isSchemaField(obj)) return;

  const record = obj as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id : undefined;

  if (id) {
    tryRecordEnumField(record, id, path, map);
  }

  const currentPath = id ? [...path, id] : path;
  recurseChildren(record, currentPath, map);
}

/**
 * Build a path-scoped options map from the schema — the single enum extractor
 * for the fields module.
 *
 * Unlike buildEnumLabelMap which creates a flat global map (causing collisions),
 * this keys by full field path, so only enum fields are matched and values
 * can't collide across fields.
 *
 * Options are kept as the schema ordered them, so an input control can render
 * the dropdown straight from here instead of hardcoding a list that drifts from
 * the proto enum.
 *
 * @param schema - The workflow schema response from the API
 * @returns A map of "group.field_id" → ordered options
 */
export function buildFieldOptionsMap(schema: unknown): FieldOptionsMap {
  const map: FieldOptionsMap = {};
  if (!schema || typeof schema !== 'object') return map;

  const record = schema as Record<string, unknown>;

  // Walk input and output sections without including "input"/"output" in the path
  // since Field components don't know their input/output context
  for (const key of ['input', 'output']) {
    if (record[key]) {
      extractFieldEnums(record[key], [], map);
    }
  }

  // If schema doesn't have input/output at top level, walk the whole thing
  if (!record.input && !record.output) {
    extractFieldEnums(schema, [], map);
  }

  return map;
}

/**
 * Collapse an options map to the value→label form used for display formatting.
 * Duplicate values are last-wins, matching the flat `buildEnumLabelMap` path.
 */
export function optionsToEnumMap(optionsMap: FieldOptionsMap): FieldEnumMap {
  const map: FieldEnumMap = {};
  for (const [path, options] of Object.entries(optionsMap)) {
    const fieldMap: Record<string, string> = {};
    for (const opt of options) {
      fieldMap[opt.value] = opt.label;
    }
    map[path] = fieldMap;
  }
  return map;
}

/**
 * Build a path-scoped enum map from the schema — a projection of
 * {@link buildFieldOptionsMap} down to value→label per field path.
 *
 * @param schema - The workflow schema response from the API
 * @returns A map of "group.field_id" → {value → label}
 */
export function buildFieldEnumMap(schema: unknown): FieldEnumMap {
  return optionsToEnumMap(buildFieldOptionsMap(schema));
}

/**
 * Returns true if `record.validation.required === true`.
 */
function isMarkedRequired(record: Record<string, unknown>): boolean {
  const validation = record.validation as { required?: boolean } | undefined;
  return validation?.required === true;
}

/**
 * Recurse into a schema record's nested arrays (`fields`, `groups`),
 * extending the current dotted path with `currentPath`.
 */
function recurseRequiredChildren(
  record: Record<string, unknown>,
  currentPath: string[],
  set: Set<string>,
): void {
  if (Array.isArray(record.fields)) {
    extractRequiredPaths(record.fields, currentPath, set);
  }
  if (Array.isArray(record.groups)) {
    extractRequiredPaths(record.groups, currentPath, set);
  }
}

/**
 * Recursively walk a schema structure and collect dotted paths of every field
 * whose `validation.required === true`. Mirrors the path conventions used by
 * `buildFieldEnumMap` so call sites can look up "did the schema mark this
 * (nested) field required?" by composing the same `pathPrefix + fieldId`
 * key they already use for enum formatting.
 */
function extractRequiredPaths(obj: unknown, path: string[], set: Set<string>): void {
  if (Array.isArray(obj)) {
    for (const item of obj) {
      extractRequiredPaths(item, path, set);
    }
    return;
  }

  if (!isSchemaField(obj)) return;

  const record = obj as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id : undefined;

  if (id && isMarkedRequired(record)) {
    set.add([...path, id].join('.'));
  }

  recurseRequiredChildren(record, id ? [...path, id] : path, set);
}

/**
 * Build a set of dotted field paths that the schema marks as required.
 *
 * Uses the same walking + path-composition rules as `buildFieldEnumMap`, so
 * consumers can ask "is `<pathPrefix>.<fieldId>` required?" with the same
 * keys they already pass to `formatFieldValue`. Useful for custom array
 * input components (sites_of_care, drug, j_code, etc.) that want to render
 * a `*` indicator only on subfields the schema says are required.
 *
 * @param schema - The workflow schema response from the API
 * @returns A Set of dotted paths (e.g. "sites_of_care.name") that are required
 */
export function buildRequiredFieldPaths(schema: unknown): Set<string> {
  const set = new Set<string>();
  if (!schema || typeof schema !== 'object') return set;

  const record = schema as Record<string, unknown>;

  for (const key of ['input', 'output']) {
    if (record[key]) {
      extractRequiredPaths(record[key], [], set);
    }
  }

  if (!record.input && !record.output) {
    extractRequiredPaths(schema, [], set);
  }

  return set;
}

/**
 * Format a value using path-scoped enum mapping.
 *
 * Only applies enum label lookup when the field's full path exists in the
 * fieldEnumMap (meaning it's a select/multi_select/radio field).
 * Numeric and text fields pass through as raw values.
 *
 * @param value - The value to format
 * @param fieldId - The field ID (e.g., "coinsurance")
 * @param pathPrefix - The path prefix from FieldPathContext (e.g., "drug.coverage")
 * @param fieldEnumMap - The path-scoped enum map from buildFieldEnumMap
 */
/** Resolve the full field path from prefix and fieldId */
function resolveFieldPath(fieldId: string | undefined, pathPrefix: string): string | undefined {
  if (!fieldId) return undefined;
  return pathPrefix ? `${pathPrefix}.${fieldId}` : fieldId;
}

/** Format an array value, applying enum labels if a field map is available */
function formatArrayFieldValue(
  value: unknown[],
  fieldMap: Record<string, string> | undefined,
): string {
  if (value.length === 0) return '--';
  return fieldMap
    ? value.map(v => formatEnumLabel(String(v), fieldMap)).join(', ')
    : value.map(String).join(', ');
}

export function formatFieldValue(
  value: unknown,
  fieldId: string | undefined,
  pathPrefix: string,
  fieldEnumMap: FieldEnumMap,
): string {
  if (value === undefined || value === null || value === '') return '--';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  const fullPath = resolveFieldPath(fieldId, pathPrefix);
  const fieldMap = fullPath ? fieldEnumMap[fullPath] : undefined;

  if (Array.isArray(value)) {
    return formatArrayFieldValue(value, fieldMap);
  }

  return fieldMap ? formatEnumLabel(String(value), fieldMap) : String(value);
}
